import type { SubstructureQuantity, SupportQuantity } from "../../../http/deckDesigner/formulas";
import { RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import { SUPPORT_FAMILY_CODE_NAG } from "../../../http/deckDesigner/types/supportFamilies";
import {
  DEFAULT_GRID_STEP_PX,
  FIT_BOUNDS_PADDING_RATIO,
  getEffectiveFitBounds,
  type PlaneBounds,
  type PlaneCoordinate,
} from "./CartesianPlane";
import type { DeckBoardSegment } from "./DeckBoardsLayer";
import {
  getCoordinateBounds,
  getLShapeGeometry,
  getSegmentLength,
  isCutoutOnRight,
  isCutoutOnTop,
  isNonNegativeNumber,
  isPositiveNumber,
  METERS_TO_MILLIMETERS,
} from "./deckGeometry";

type JoistKind = "long" | "short";

export type JoistSegment = {
  end: PlaneCoordinate;
  id: string;
  kind: JoistKind;
  start: PlaneCoordinate;
};

export type SupportPoint = PlaneCoordinate & {
  id: string;
};

export type ConnectorPoint = PlaneCoordinate & {
  id: string;
};

export type BottomViewLayout = {
  joists: JoistSegment[];
  supports: SupportPoint[];
};

export type SupportSpacingInfo = {
  long: number | null;
  short: number | null;
};

const DEFAULT_JOIST_WIDTH_M = 6 / DEFAULT_GRID_STEP_PX;

const getPositiveCount = (value: number | null | undefined): number =>
  isPositiveNumber(value) ? Math.round(value) : 0;

export const getJoistWidthMeters = (substructureBaseMm: number | null | undefined): number =>
  isPositiveNumber(substructureBaseMm) ? substructureBaseMm / METERS_TO_MILLIMETERS : DEFAULT_JOIST_WIDTH_M;

type Orientation = "horizontal" | "vertical";

// For axis-aligned segments only (joists, dimension lines): vertical when it keeps its x.
const getSegmentOrientation = (segment: { start: PlaneCoordinate; end: PlaneCoordinate }): Orientation =>
  segment.start.x === segment.end.x ? "vertical" : "horizontal";

const lerpPlanePoint = (start: PlaneCoordinate, end: PlaneCoordinate, ratio: number): PlaneCoordinate => ({
  x: start.x + (end.x - start.x) * ratio,
  y: start.y + (end.y - start.y) * ratio,
});

const createInclusivePositions = (min: number, max: number, count: number): number[] => {
  if (count <= 0 || max <= min) {
    return [];
  }

  if (count === 1) {
    return [(min + max) / 2];
  }

  return Array.from({ length: count }, (_, index) => min + ((max - min) * index) / (count - 1));
};

const createPositionsExcludingStart = (min: number, max: number, count: number): number[] => {
  if (count <= 0 || max <= min) {
    return [];
  }

  return Array.from({ length: count }, (_, index) => min + ((max - min) * (index + 1)) / count);
};

const createPositionsExcludingEnd = (min: number, max: number, count: number): number[] => {
  if (count <= 0 || max <= min) {
    return [];
  }

  return Array.from({ length: count }, (_, index) => min + ((max - min) * index) / count);
};

const createVerticalJoists = (
  positions: number[],
  minY: number,
  maxY: number,
  kind: JoistKind,
  idPrefix: string,
): JoistSegment[] =>
  positions.map((x, index) => ({
    id: `${idPrefix}-${index}`,
    kind,
    start: { x, y: minY },
    end: { x, y: maxY },
  }));

const createHorizontalJoists = (
  positions: number[],
  minX: number,
  maxX: number,
  kind: JoistKind,
  idPrefix: string,
): JoistSegment[] =>
  positions.map((y, index) => ({
    id: `${idPrefix}-${index}`,
    kind,
    start: { x: minX, y },
    end: { x: maxX, y },
  }));

const getJoistLength = (joist: JoistSegment): number => getSegmentLength(joist.start, joist.end);

const getSupportPointCount = (
  segment: JoistSegment,
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): number => {
  // A stripe-type support (e.g. GRANULO100/ROLL/MAT110) is a continuous roll/mat covering many
  // support points at once, not discrete point supports - so it has no meaningful points to render.
  if (!supportQuantity || isStripeSupport) {
    return 0;
  }

  if (supportFamilyCode === SUPPORT_FAMILY_CODE_NAG && supportQuantity.maxSpacingCm > 0) {
    return Math.floor((getJoistLength(segment) * 100) / supportQuantity.maxSpacingCm) + 1;
  }

  return getPositiveCount(segment.kind === "long" ? supportQuantity.long : supportQuantity.short);
};

const createSupportPoints = (
  joists: JoistSegment[],
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): SupportPoint[] =>
  joists.flatMap((joist) => {
    const count = getSupportPointCount(joist, supportQuantity, supportFamilyCode, isStripeSupport);

    if (count <= 0) {
      return [];
    }

    return Array.from({ length: count }, (_, index) => {
      const ratio = count === 1 ? 0.5 : index / (count - 1);

      return {
        id: `${joist.id}-support-${index}`,
        x: joist.start.x + (joist.end.x - joist.start.x) * ratio,
        y: joist.start.y + (joist.end.y - joist.start.y) * ratio,
      };
    });
  });

const getSupportSpacingForKind = (
  joists: JoistSegment[],
  kind: JoistKind,
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): number | null => {
  const joist = joists.find((segment) => segment.kind === kind);

  if (!joist) {
    return null;
  }

  const supportPointCount = getSupportPointCount(joist, supportQuantity, supportFamilyCode, isStripeSupport);

  return supportPointCount > 1 ? getJoistLength(joist) / (supportPointCount - 1) : null;
};

export const getSupportSpacingInfo = (
  joists: JoistSegment[],
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): SupportSpacingInfo => ({
  long: getSupportSpacingForKind(joists, "long", supportQuantity, supportFamilyCode, isStripeSupport),
  short: getSupportSpacingForKind(joists, "short", supportQuantity, supportFamilyCode, isStripeSupport),
});

export type MeasurementSegment = {
  end: PlaneCoordinate;
  start: PlaneCoordinate;
};

export type JoistMeasurementExample = {
  joist: JoistSegment;
  joistInnerEndpoint: PlaneCoordinate;
  joistOuterEndpoint: PlaneCoordinate;
  joistSpacingMeters: number | null;
  joistSpacingSegment: MeasurementSegment | null;
  kind: JoistKind;
  supportCount: number;
  supportSpacingMeters: number | null;
  supportSpacingSegment: MeasurementSegment | null;
};

const getCrossAxisCoordinate = (joist: JoistSegment, orientation: Orientation): number =>
  orientation === "vertical" ? joist.start.x : joist.start.y;

const getLengthAxisCoordinate = (point: PlaneCoordinate, orientation: Orientation): number =>
  orientation === "vertical" ? point.y : point.x;

// Orders a kind's joists by distance from the other kind's joists, outermost (farthest from the
// L-shape's inner corner - or, on a rectangular deck with no other kind, in their natural order,
// which already runs from one true outer edge inward) first.
const sortJoistsByOuterness = (joists: JoistSegment[], kind: JoistKind, orientation: Orientation): JoistSegment[] => {
  const sameKind = joists.filter((joist) => joist.kind === kind);
  const otherKindCoordinates = joists
    .filter((joist) => joist.kind !== kind)
    .map((joist) => getCrossAxisCoordinate(joist, orientation));

  if (otherKindCoordinates.length === 0) {
    return sameKind;
  }

  const referenceCoordinate = (Math.min(...otherKindCoordinates) + Math.max(...otherKindCoordinates)) / 2;

  return [...sameKind].sort(
    (a, b) =>
      Math.abs(getCrossAxisCoordinate(b, orientation) - referenceCoordinate) -
      Math.abs(getCrossAxisCoordinate(a, orientation) - referenceCoordinate),
  );
};

// A short joist's own span may run from the deck's true outer edge to the L-shape's inner 90-degree
// corner - which endpoint is "start" vs "end" depends on the cutout's orientation, so the long
// joists' full span (edge to edge, no inner corner) is the ground truth for where the real outer
// edge is: whichever of this joist's endpoints sits at that boundary (rather than strictly inside
// it) is the true outer end.
const getOuterOrderedEndpoints = (
  joist: JoistSegment,
  joists: JoistSegment[],
  orientation: Orientation,
): { outer: PlaneCoordinate; inner: PlaneCoordinate } => {
  const startCoordinate = getLengthAxisCoordinate(joist.start, orientation);
  const endCoordinate = getLengthAxisCoordinate(joist.end, orientation);
  const boundaryCoordinates = joists
    .filter((segment) => segment.kind === "long")
    .flatMap((segment) => [
      getLengthAxisCoordinate(segment.start, orientation),
      getLengthAxisCoordinate(segment.end, orientation),
    ])
    .concat(startCoordinate, endCoordinate);
  const overallMin = Math.min(...boundaryCoordinates);
  const overallMax = Math.max(...boundaryCoordinates);
  const distanceToBoundary = (coordinate: number) =>
    Math.min(Math.abs(coordinate - overallMin), Math.abs(coordinate - overallMax));

  return distanceToBoundary(startCoordinate) <= distanceToBoundary(endCoordinate)
    ? { outer: joist.start, inner: joist.end }
    : { outer: joist.end, inner: joist.start };
};

// Anchored on the outermost joist's own true outer edge - the actual support marker closest to that
// edge and its immediate neighbor, a real adjacent pair regardless of which end of the array they
// happen to be (spacing is uniform, so any adjacent pair is a faithful example).
const getSupportSpacingExample = (
  joist: JoistSegment,
  joists: JoistSegment[],
  orientation: Orientation,
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): { count: number; segment: MeasurementSegment | null; spacingMeters: number | null } => {
  const count = getSupportPointCount(joist, supportQuantity, supportFamilyCode, isStripeSupport);

  if (count < 2) {
    return { count, spacingMeters: null, segment: null };
  }

  const { outer, inner } = getOuterOrderedEndpoints(joist, joists, orientation);

  return {
    count,
    spacingMeters: getJoistLength(joist) / (count - 1),
    segment: { start: outer, end: lerpPlanePoint(outer, inner, 1 / (count - 1)) },
  };
};

// Which length-axis side (a joist's own start/end - long joists always span the deck's full extent,
// so this mapping never flips the way it can for a single short joist) the L-shape's inner corner
// sits on: whichever side the short joists fall short of the deck's true extent. Null on a
// rectangular deck (no short joists, no inner corner - any side is equally fine).
const getNotchLengthAxisSide = (joists: JoistSegment[], orientation: Orientation): "start" | "end" | null => {
  const longCoordinates = joists
    .filter((joist) => joist.kind === "long")
    .flatMap((joist) => [
      getLengthAxisCoordinate(joist.start, orientation),
      getLengthAxisCoordinate(joist.end, orientation),
    ]);
  const shortCoordinates = joists
    .filter((joist) => joist.kind === "short")
    .flatMap((joist) => [
      getLengthAxisCoordinate(joist.start, orientation),
      getLengthAxisCoordinate(joist.end, orientation),
    ]);

  if (shortCoordinates.length === 0) {
    return null;
  }

  const overallMin = Math.min(...longCoordinates, ...shortCoordinates);
  const overallMax = Math.max(...longCoordinates, ...shortCoordinates);
  const shortMin = Math.min(...shortCoordinates);
  const shortMax = Math.max(...shortCoordinates);

  return shortMin - overallMin > overallMax - shortMax ? "start" : "end";
};

// The long-to-long example: the second bay from the deck's outer edge (not the outermost one, which
// already carries the support-spacing/count labels), positioned right on the joist's own edge on the
// side where the L-shape's inner corner is. On a rectangular deck there's no such corner to prefer,
// so it deliberately uses the opposite side from the support-spacing/count labels (which - see
// getOuterOrderedEndpoints's tie-break - always land on "start") instead of landing on the same one.
const getLongJoistSpacingExample = (
  orderedLongJoists: JoistSegment[],
  joists: JoistSegment[],
  orientation: Orientation,
): { segment: MeasurementSegment; spacingMeters: number } | null => {
  const bayIndex = Math.min(1, Math.max(orderedLongJoists.length - 2, 0));
  const reference = orderedLongJoists[bayIndex];
  const neighbor = orderedLongJoists[bayIndex + 1];

  if (!reference || !neighbor) {
    return null;
  }

  const referenceCoordinate = getCrossAxisCoordinate(reference, orientation);
  const neighborCoordinate = getCrossAxisCoordinate(neighbor, orientation);
  const spacingMeters = Math.abs(neighborCoordinate - referenceCoordinate);
  const notchSide = getNotchLengthAxisSide(joists, orientation);
  const outer = notchSide === "start" ? reference.start : reference.end;
  const lengthCoordinate = getLengthAxisCoordinate(outer, orientation);

  if (orientation === "vertical") {
    return {
      spacingMeters,
      segment: {
        start: { x: referenceCoordinate, y: lengthCoordinate },
        end: { x: neighborCoordinate, y: lengthCoordinate },
      },
    };
  }

  return {
    spacingMeters,
    segment: {
      start: { x: lengthCoordinate, y: referenceCoordinate },
      end: { x: lengthCoordinate, y: neighborCoordinate },
    },
  };
};

// The short-to-short example: the short joist right after the outermost one, and whichever joist is
// next further inward - long or short, it doesn't matter, since a long joist right where the short
// group runs out sits exactly one short-spacing step away (the two groups' positions line up at the
// boundary between them). With only one short joist, that single joist stands in for "the one after
// the outermost" too.
const getShortJoistSpacingExample = (
  joists: JoistSegment[],
  orientation: Orientation,
): { segment: MeasurementSegment; spacingMeters: number } | null => {
  const orderedShort = sortJoistsByOuterness(joists, "short", orientation);
  const [outermostShort, secondShort] = orderedShort;

  if (!outermostShort) {
    return null;
  }

  const reference = secondShort ?? outermostShort;
  const referenceCoordinate = getCrossAxisCoordinate(reference, orientation);

  // Which way "further inward" (toward the long joists) is, from the reference joist's position -
  // not "farther from the long range's midpoint", which ties between the two ends of the long range
  // and can end up pointing at the far outer long joist instead of the adjacent one.
  const otherCoordinates = joists
    .filter((joist) => joist.kind !== "short")
    .map((joist) => getCrossAxisCoordinate(joist, orientation));
  const longRangeMid =
    otherCoordinates.length > 0
      ? (Math.min(...otherCoordinates) + Math.max(...otherCoordinates)) / 2
      : referenceCoordinate;
  const inwardSign = Math.sign(longRangeMid - referenceCoordinate) || 1;

  // The nearest joist of any kind strictly further inward than the reference - genuinely the next
  // one over, not just "farther from the long range's midpoint" (see above).
  const neighbor = joists.reduce<JoistSegment | null>((closest, joist) => {
    if (joist === reference) {
      return closest;
    }

    const signedDistance = (getCrossAxisCoordinate(joist, orientation) - referenceCoordinate) * inwardSign;

    if (signedDistance <= 0) {
      return closest;
    }

    if (!closest) {
      return joist;
    }

    const closestSignedDistance = (getCrossAxisCoordinate(closest, orientation) - referenceCoordinate) * inwardSign;

    return signedDistance < closestSignedDistance ? joist : closest;
  }, null);

  if (!neighbor) {
    return null;
  }

  const neighborCoordinate = getCrossAxisCoordinate(neighbor, orientation);
  const spacingMeters = Math.abs(neighborCoordinate - referenceCoordinate);
  const { outer } = getOuterOrderedEndpoints(reference, joists, orientation);
  const lengthCoordinate = getLengthAxisCoordinate(outer, orientation);

  if (orientation === "vertical") {
    return {
      spacingMeters,
      segment: {
        start: { x: referenceCoordinate, y: lengthCoordinate },
        end: { x: neighborCoordinate, y: lengthCoordinate },
      },
    };
  }

  return {
    spacingMeters,
    segment: {
      start: { x: lengthCoordinate, y: referenceCoordinate },
      end: { x: lengthCoordinate, y: neighborCoordinate },
    },
  };
};

// One example per joist kind (long, and short when the shape has any) - not every joist/support
// pair, just enough to show the pattern: the joist-to-joist gap, the support-to-support gap on the
// outermost joist, and how many supports sit on it.
export const getBottomViewMeasurementExamples = (
  joists: JoistSegment[],
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): JoistMeasurementExample[] =>
  (["long", "short"] as const).flatMap((kind) => {
    const [firstOfKind] = joists.filter((joist) => joist.kind === kind);

    if (!firstOfKind) {
      return [];
    }

    const orientation = getSegmentOrientation(firstOfKind);
    const orderedJoists = sortJoistsByOuterness(joists, kind, orientation);
    const [outermost] = orderedJoists;

    if (!outermost) {
      return [];
    }

    const joistSpacing =
      kind === "long"
        ? getLongJoistSpacingExample(orderedJoists, joists, orientation)
        : getShortJoistSpacingExample(joists, orientation);
    const supportSpacing = getSupportSpacingExample(
      outermost,
      joists,
      orientation,
      supportQuantity,
      supportFamilyCode,
      isStripeSupport,
    );
    const { outer: joistOuterEndpoint, inner: joistInnerEndpoint } = getOuterOrderedEndpoints(
      outermost,
      joists,
      orientation,
    );

    return [
      {
        joist: outermost,
        joistInnerEndpoint,
        joistOuterEndpoint,
        kind,
        joistSpacingMeters: joistSpacing?.spacingMeters ?? null,
        joistSpacingSegment: joistSpacing?.segment ?? null,
        supportCount: supportSpacing.count,
        supportSpacingMeters: supportSpacing.spacingMeters,
        supportSpacingSegment: supportSpacing.segment,
      },
    ];
  });

// A joist/support-spacing segment currently runs right along the joist it measures (see
// getSupportSpacingExample/getLongJoistSpacingExample above) - a real architectural dimension line
// instead sits alongside it, offset by a fixed distance, with extension lines bridging back to the
// true reference points. This is that offset, in plane meters (not pixels) so both the live SVG
// render and the PDF's independent, bounds-normalized label placement agree on where it lands.
export const DIMENSION_LINE_OFFSET_METERS = 0.18;

// The export capture's fitted view pads each axis by FIT_BOUNDS_PADDING_RATIO of that axis's own
// length (see CartesianPlane's computeFitView) - a fixed-meters offset can exceed that margin for a
// deck whose relevant side is short (e.g. a long, narrow deck), pushing the dimension line past the
// fitted viewport where the plane's overflow:hidden clips it. Capping the offset at a fraction of
// that margin (not the whole thing, to leave slack for the extension line's own small overshoot)
// keeps it inside the viewport regardless of the deck's proportions.
const DIMENSION_LINE_OFFSET_MARGIN_SAFETY_FACTOR = 0.7;

// "Outward" is decided the same way getLabelPlacement's on-screen label picks a side - away from the
// shape's own center - so the offset dimension line and its label always end up on the same side.
export const getOffsetMeasurementSegment = (segment: MeasurementSegment, bounds: PlaneBounds): MeasurementSegment => {
  const orientation = getSegmentOrientation(segment);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  // The margin available is measured against the same widened frame CartesianPlane actually fits the
  // export capture to (see getEffectiveFitBounds) - an extremely long, narrow deck's raw bounds would
  // otherwise cap this offset down to nearly nothing, even though the real, widened viewport has
  // plenty of room.
  const effectiveBounds = getEffectiveFitBounds(bounds);
  const maxOffsetX =
    (effectiveBounds.maxX - effectiveBounds.minX) *
    FIT_BOUNDS_PADDING_RATIO *
    DIMENSION_LINE_OFFSET_MARGIN_SAFETY_FACTOR;
  const maxOffsetY =
    (effectiveBounds.maxY - effectiveBounds.minY) *
    FIT_BOUNDS_PADDING_RATIO *
    DIMENSION_LINE_OFFSET_MARGIN_SAFETY_FACTOR;

  const offset =
    orientation === "vertical"
      ? {
          x: (segment.start.x >= centerX ? 1 : -1) * Math.min(DIMENSION_LINE_OFFSET_METERS, maxOffsetX),
          y: 0,
        }
      : {
          x: 0,
          y: (segment.start.y >= centerY ? 1 : -1) * Math.min(DIMENSION_LINE_OFFSET_METERS, maxOffsetY),
        };

  return {
    start: { x: segment.start.x + offset.x, y: segment.start.y + offset.y },
    end: { x: segment.end.x + offset.x, y: segment.end.y + offset.y },
  };
};

// text is whatever the caller's formatters return: a string on screen, a language-neutral value
// for the PDF export.
export type BottomViewMeasurementLabel<T = string> = {
  key: string;
  point: PlaneCoordinate;
  orientation: Orientation;
  rotate: boolean;
  text: T;
};

// Pure data version of what BottomMeasurementsLayer renders on screen - used by the PDF export to
// draw the same labels as crisp vector text instead of baking them into the captured raster.
//
// Placement priority: a spacing label (joist gap, support gap) is anchored right on the dashed line
// it measures - it has to stay there to mean anything, so every one of those is placed first. A
// support-count label isn't tied to any drawn line, so it goes last, starting from a plain anchor
// (the joist's own midpoint) and letting writeImageLabels' own collision search (which only avoids
// labels placed *before* it, in array order) nudge it clear of whichever spacing labels already
// claimed the spot - instead of pre-guessing a "safe" offset by hand per joist kind.
export const getBottomViewMeasurementLabels = <T = string>(
  examples: JoistMeasurementExample[],
  bounds: PlaneBounds,
  formatSpacing: (spacingMeters: number) => T,
  // The whole support-count label, e.g. "Supports: 4 pcs".
  formatSupportCount: (supportCount: number) => T,
): BottomViewMeasurementLabel<T>[] => {
  const spacingLabels: BottomViewMeasurementLabel<T>[] = [];
  const countLabels: BottomViewMeasurementLabel<T>[] = [];

  examples.forEach((example) => {
    const joistOrientation = getSegmentOrientation(example.joist);

    if (example.joistSpacingSegment && example.joistSpacingMeters != null) {
      const orientation = getSegmentOrientation(example.joistSpacingSegment);
      const offsetSegment = getOffsetMeasurementSegment(example.joistSpacingSegment, bounds);
      spacingLabels.push({
        key: `${example.kind}-joist-gap-label`,
        point: lerpPlanePoint(offsetSegment.start, offsetSegment.end, 0.5),
        orientation,
        // A vertical joist-spacing segment sits right at the deck's own vertical edge, next to the
        // dimension line marking it - rotating the text to run alongside that edge (like a top-view
        // vertical dimension) keeps it clear of the line instead of a wide horizontal box crossing it.
        rotate: orientation === "vertical",
        text: formatSpacing(example.joistSpacingMeters),
      });
    }

    if (example.supportSpacingSegment && example.supportSpacingMeters != null) {
      const orientation = getSegmentOrientation(example.supportSpacingSegment);
      const offsetSegment = getOffsetMeasurementSegment(example.supportSpacingSegment, bounds);
      spacingLabels.push({
        key: `${example.kind}-support-gap-label`,
        point: lerpPlanePoint(offsetSegment.start, offsetSegment.end, 0.5),
        orientation,
        rotate: orientation === "vertical",
        text: formatSpacing(example.supportSpacingMeters),
      });
    }

    if (example.supportCount > 0) {
      // Anchored directly on the joist otherwise (its own endpoints) - pushed off to the side the
      // same way the two dimension lines are, so the box doesn't sit on top of the joist/support dot
      // it's labelling.
      const offsetJoistSegment = getOffsetMeasurementSegment(
        { start: example.joistOuterEndpoint, end: example.joistInnerEndpoint },
        bounds,
      );
      countLabels.push({
        key: `${example.kind}-support-count-label`,
        point: lerpPlanePoint(offsetJoistSegment.start, offsetJoistSegment.end, 0.5),
        orientation: joistOrientation,
        rotate: joistOrientation === "vertical",
        text: formatSupportCount(example.supportCount),
      });
    }
  });

  return [...spacingLabels, ...countLabels];
};

const createRectangularJoists = (
  coordinates: PlaneCoordinate[],
  hasHorizontalBoards: boolean | null,
  substructureQuantity: SubstructureQuantity | null,
): JoistSegment[] => {
  const bounds = getCoordinateBounds(coordinates);
  const totalCount = getPositiveCount(substructureQuantity?.total);

  if (!bounds || totalCount <= 0) {
    return [];
  }

  if (hasHorizontalBoards !== false) {
    return createVerticalJoists(
      createInclusivePositions(bounds.minX, bounds.maxX, totalCount),
      bounds.minY,
      bounds.maxY,
      "long",
      "rectangular-vertical",
    );
  }

  return createHorizontalJoists(
    createInclusivePositions(bounds.minY, bounds.maxY, totalCount),
    bounds.minX,
    bounds.maxX,
    "long",
    "rectangular-horizontal",
  );
};

const createLShapeJoists = (
  coordinates: PlaneCoordinate[],
  hasHorizontalBoards: boolean | null,
  substructureQuantity: SubstructureQuantity | null,
): JoistSegment[] => {
  const geometry = getLShapeGeometry(coordinates);
  const longCount = getPositiveCount(substructureQuantity?.long);
  const shortCount = getPositiveCount(substructureQuantity?.short);

  if (!geometry || longCount + shortCount <= 0) {
    return [];
  }

  const { bounds, cutoutCorner, innerCorner } = geometry;
  const cutoutOnRight = isCutoutOnRight(cutoutCorner);
  const cutoutOnTop = isCutoutOnTop(cutoutCorner);

  if (hasHorizontalBoards !== false) {
    const longPositions = cutoutOnRight
      ? createInclusivePositions(bounds.minX, innerCorner.x, longCount)
      : createInclusivePositions(innerCorner.x, bounds.maxX, longCount);
    const shortPositions = cutoutOnRight
      ? createPositionsExcludingStart(innerCorner.x, bounds.maxX, shortCount)
      : createPositionsExcludingEnd(bounds.minX, innerCorner.x, shortCount);
    const shortMinY = cutoutOnTop ? bounds.minY : innerCorner.y;
    const shortMaxY = cutoutOnTop ? innerCorner.y : bounds.maxY;

    return [
      ...createVerticalJoists(longPositions, bounds.minY, bounds.maxY, "long", "l-vertical-long"),
      ...createVerticalJoists(shortPositions, shortMinY, shortMaxY, "short", "l-vertical-short"),
    ];
  }

  const longPositions = cutoutOnTop
    ? createInclusivePositions(bounds.minY, innerCorner.y, longCount)
    : createInclusivePositions(innerCorner.y, bounds.maxY, longCount);
  const shortPositions = cutoutOnTop
    ? createPositionsExcludingStart(innerCorner.y, bounds.maxY, shortCount)
    : createPositionsExcludingEnd(bounds.minY, innerCorner.y, shortCount);
  const shortMinX = cutoutOnRight ? bounds.minX : innerCorner.x;
  const shortMaxX = cutoutOnRight ? innerCorner.x : bounds.maxX;

  return [
    ...createHorizontalJoists(longPositions, bounds.minX, bounds.maxX, "long", "l-horizontal-long"),
    ...createHorizontalJoists(shortPositions, shortMinX, shortMaxX, "short", "l-horizontal-short"),
  ];
};

export const createBottomViewLayout = (
  coordinates: PlaneCoordinate[],
  selectedShape: string,
  hasHorizontalBoards: boolean | null,
  substructureQuantity: SubstructureQuantity | null,
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
): BottomViewLayout => {
  const joists =
    selectedShape === RECTANGULAR_SHAPE
      ? createRectangularJoists(coordinates, hasHorizontalBoards, substructureQuantity)
      : createLShapeJoists(coordinates, hasHorizontalBoards, substructureQuantity);

  return {
    joists,
    supports: createSupportPoints(joists, supportQuantity, supportFamilyCode, isStripeSupport),
  };
};

const SEAM_POSITION_TOLERANCE_METERS = 0.001;

type BoardSeam = {
  axis: "x" | "y";
  id: string;
  position: number;
  spanMax: number;
  spanMin: number;
};

const getBoardsWithBounds = (boards: DeckBoardSegment[]): Array<{ board: DeckBoardSegment; bounds: PlaneBounds }> =>
  boards.flatMap((board) => {
    const bounds = getCoordinateBounds(board.corners);

    return bounds ? [{ board, bounds }] : [];
  });

const pushSeamIfSpanValid = (
  seams: BoardSeam[],
  axis: "x" | "y",
  id: string,
  position: number,
  spanMin: number,
  spanMax: number,
): void => {
  if (spanMax - spanMin > SEAM_POSITION_TOLERANCE_METERS) {
    seams.push({ axis, id, position, spanMax, spanMin });
  }
};

// Boards that visually touch (separated by at most the configured gap) share a seam where a
// connector goes wherever it crosses a joist. A board's own outer edge, with no neighbor to
// touch (the outermost boards, or either side of a gap wider than the configured board gap),
// is also a seam on its own: it still needs a connector at every joist crossing.
const getBoardSeams = (boards: DeckBoardSegment[], isHorizontal: boolean, gapMeters: number): BoardSeam[] => {
  const boardsWithBounds = getBoardsWithBounds(boards);
  const maxSeamGap = gapMeters + SEAM_POSITION_TOLERANCE_METERS;
  const axis: "x" | "y" = isHorizontal ? "y" : "x";

  const sorted = [...boardsWithBounds].sort((first, second) =>
    isHorizontal ? second.bounds.maxY - first.bounds.maxY : first.bounds.minX - second.bounds.minX,
  );

  const seams: BoardSeam[] = [];

  sorted.forEach((entry, index) => {
    const spanMin = isHorizontal ? entry.bounds.minX : entry.bounds.minY;
    const spanMax = isHorizontal ? entry.bounds.maxX : entry.bounds.maxY;

    if (index === 0) {
      const outerEdge = isHorizontal ? entry.bounds.maxY : entry.bounds.minX;

      pushSeamIfSpanValid(seams, axis, `${entry.board.id}-edge-start`, outerEdge, spanMin, spanMax);
    }

    const next = sorted[index + 1];

    if (!next) {
      const outerEdge = isHorizontal ? entry.bounds.minY : entry.bounds.maxX;

      pushSeamIfSpanValid(seams, axis, `${entry.board.id}-edge-end`, outerEdge, spanMin, spanMax);

      return;
    }

    const nextSpanMin = isHorizontal ? next.bounds.minX : next.bounds.minY;
    const nextSpanMax = isHorizontal ? next.bounds.maxX : next.bounds.maxY;
    const seamGap = isHorizontal ? entry.bounds.minY - next.bounds.maxY : next.bounds.minX - entry.bounds.maxX;

    if (seamGap < -SEAM_POSITION_TOLERANCE_METERS || seamGap > maxSeamGap) {
      const entryOuterEdge = isHorizontal ? entry.bounds.minY : entry.bounds.maxX;
      const nextOuterEdge = isHorizontal ? next.bounds.maxY : next.bounds.minX;

      pushSeamIfSpanValid(seams, axis, `${entry.board.id}-edge-end`, entryOuterEdge, spanMin, spanMax);
      pushSeamIfSpanValid(seams, axis, `${next.board.id}-edge-start`, nextOuterEdge, nextSpanMin, nextSpanMax);

      return;
    }

    const overlapSpanMin = Math.max(spanMin, nextSpanMin);
    const overlapSpanMax = Math.min(spanMax, nextSpanMax);
    const position = isHorizontal
      ? (entry.bounds.minY + next.bounds.maxY) / 2
      : (entry.bounds.maxX + next.bounds.minX) / 2;

    pushSeamIfSpanValid(seams, axis, `${entry.board.id}-${next.board.id}`, position, overlapSpanMin, overlapSpanMax);
  });

  return seams;
};

const getConnectorPointsForSeam = (seam: BoardSeam, joists: JoistSegment[]): ConnectorPoint[] =>
  joists.flatMap((joist) => {
    if (seam.axis === "y") {
      if (getSegmentOrientation(joist) !== "vertical") {
        return [];
      }

      const x = joist.start.x;

      if (x < seam.spanMin || x > seam.spanMax) {
        return [];
      }

      const joistMinY = Math.min(joist.start.y, joist.end.y);
      const joistMaxY = Math.max(joist.start.y, joist.end.y);

      if (seam.position < joistMinY || seam.position > joistMaxY) {
        return [];
      }

      return [{ id: `${seam.id}-${joist.id}`, x, y: seam.position }];
    }

    if (getSegmentOrientation(joist) !== "horizontal") {
      return [];
    }

    const y = joist.start.y;

    if (y < seam.spanMin || y > seam.spanMax) {
      return [];
    }

    const joistMinX = Math.min(joist.start.x, joist.end.x);
    const joistMaxX = Math.max(joist.start.x, joist.end.x);

    if (seam.position < joistMinX || seam.position > joistMaxX) {
      return [];
    }

    return [{ id: `${seam.id}-${joist.id}`, x: seam.position, y }];
  });

// Visible fastening screws through the board face, so one connector goes wherever a board
// crosses a joist, centered on the board's own width at that joist (not on a seam with a
// neighbor).
const getConnectorPointsForBoard = (board: DeckBoardSegment, joists: JoistSegment[]): ConnectorPoint[] => {
  const bounds = getCoordinateBounds(board.corners);

  if (!bounds) {
    return [];
  }

  return joists.flatMap((joist) => {
    if (getSegmentOrientation(joist) === "vertical") {
      const x = joist.start.x;

      if (x < bounds.minX || x > bounds.maxX) {
        return [];
      }

      const joistMinY = Math.min(joist.start.y, joist.end.y);
      const joistMaxY = Math.max(joist.start.y, joist.end.y);
      const overlapMinY = Math.max(bounds.minY, joistMinY);
      const overlapMaxY = Math.min(bounds.maxY, joistMaxY);

      if (overlapMaxY <= overlapMinY) {
        return [];
      }

      return [{ id: `${board.id}-${joist.id}`, x, y: (overlapMinY + overlapMaxY) / 2 }];
    }

    const y = joist.start.y;

    if (y < bounds.minY || y > bounds.maxY) {
      return [];
    }

    const joistMinX = Math.min(joist.start.x, joist.end.x);
    const joistMaxX = Math.max(joist.start.x, joist.end.x);
    const overlapMinX = Math.max(bounds.minX, joistMinX);
    const overlapMaxX = Math.min(bounds.maxX, joistMaxX);

    if (overlapMaxX <= overlapMinX) {
      return [];
    }

    return [{ id: `${board.id}-${joist.id}`, x: (overlapMinX + overlapMaxX) / 2, y }];
  });
};

export const createConnectorPoints = (
  boards: DeckBoardSegment[],
  joists: JoistSegment[],
  hasHorizontalBoards: boolean | null,
  gapMillimeters: number | null,
  isVisibleFastening: boolean,
): ConnectorPoint[] => {
  if (isVisibleFastening) {
    return boards.flatMap((board) => getConnectorPointsForBoard(board, joists));
  }

  const isHorizontal = hasHorizontalBoards !== false;
  const gapMeters = isNonNegativeNumber(gapMillimeters) ? gapMillimeters / METERS_TO_MILLIMETERS : 0;
  const seams = getBoardSeams(boards, isHorizontal, gapMeters);

  return seams.flatMap((seam) => getConnectorPointsForSeam(seam, joists));
};

// Support node types for the support-reaction table (see "Regola per ricavare le azioni alla base dei
// supporti"): 1-4 on long joists (corner, edge, side, central), 5 on the last long joist where the
// short joists begin (L corner), 6-9 the same as 1-4 on short joists, 10/11 on the last long joist
// alongside the short joists (intermediate / end support).
export type SupportNodeType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export type SupportReaction = {
  point: PlaneCoordinate;
  nodeType: SupportNodeType;
  reactionKn: number;
  // What the reaction was computed from, one term per side with a neighbouring joist alongside:
  // reaction = qk · Σ (joistSpacing / 2 · lengthAlongJoist), in metres - for showing the formula.
  terms: { joistSpacing: number; lengthAlongJoist: number }[];
};

const REACTION_LENGTH_EPSILON_METERS = 0.0005;

const getOverlapLength = (aMin: number, aMax: number, bMin: number, bMax: number): number =>
  Math.max(0, Math.min(aMax, bMax) - Math.max(aMin, bMin));

// Reaction at the foot of each point support: live load (qk, kN/m²) times the support's tributary
// area - half the gap to the neighbouring support along its joist on each side, times half the gap
// to the neighbouring joist on each side, the latter counted only over the stretch where that
// neighbour actually runs (dead load of boards and joists is neglected, per the rule). With the
// supports laid out as in the bottom view, this gives exactly the rule's per-node formulas (e.g.
// node 1: qk · s1/2 · i/2, node 5: qk · (s1/2 · i + s2/2 · i/2)), and also covers a support that
// doesn't sit exactly level with the L's inner corner. Stripe supports (GRANULO rolls/mats) have no
// point supports, so they get none.
export const computeSupportReactions = (
  joists: JoistSegment[],
  supportQuantity: SupportQuantity | null,
  supportFamilyCode: string | null | undefined,
  isStripeSupport: boolean,
  liveLoadKnPerSquareMeter: number,
): SupportReaction[] => {
  const [firstJoist] = joists;

  if (!firstJoist || isStripeSupport || !(liveLoadKnPerSquareMeter > 0)) {
    return [];
  }

  const orientation = getSegmentOrientation(firstJoist);
  const lengthRange = (joist: JoistSegment) => {
    const start = getLengthAxisCoordinate(joist.start, orientation);
    const end = getLengthAxisCoordinate(joist.end, orientation);
    return { min: Math.min(start, end), max: Math.max(start, end) };
  };

  return joists.flatMap((joist) => {
    const count = getSupportPointCount(joist, supportQuantity, supportFamilyCode, isStripeSupport);

    if (count <= 0) {
      return [];
    }

    const cross = getCrossAxisCoordinate(joist, orientation);
    const range = lengthRange(joist);
    const step = count > 1 ? (range.max - range.min) / (count - 1) : 0;

    // The nearest joist on each side that runs alongside this one (overlapping length ranges).
    const neighbors = ([-1, 1] as const).map((side) =>
      joists.reduce<{ joist: JoistSegment; distance: number } | null>((closest, candidate) => {
        const distance = (getCrossAxisCoordinate(candidate, orientation) - cross) * side;
        const candidateRange = lengthRange(candidate);

        if (
          candidate === joist ||
          distance <= REACTION_LENGTH_EPSILON_METERS ||
          getOverlapLength(range.min, range.max, candidateRange.min, candidateRange.max) <= 0
        ) {
          return closest;
        }

        return !closest || distance < closest.distance ? { joist: candidate, distance } : closest;
      }, null),
    );

    return Array.from({ length: count }, (_, index): SupportReaction => {
      const position = count > 1 ? range.min + step * index : (range.min + range.max) / 2;
      const isEnd = count === 1 || index === 0 || index === count - 1;
      const intervalMin = count > 1 ? Math.max(range.min, position - step / 2) : range.min;
      const intervalMax = count > 1 ? Math.min(range.max, position + step / 2) : range.max;
      const intervalLength = intervalMax - intervalMin;

      const sides = neighbors.map((neighbor) => {
        if (!neighbor) {
          return { area: 0, coverage: 0, distance: 0, kind: null as JoistKind | null };
        }

        const neighborRange = lengthRange(neighbor.joist);
        const coverage = getOverlapLength(intervalMin, intervalMax, neighborRange.min, neighborRange.max);

        return {
          area: (neighbor.distance / 2) * coverage,
          coverage,
          distance: neighbor.distance,
          kind: neighbor.joist.kind,
        };
      });

      const coveredSides = sides.filter((side) => side.coverage > REACTION_LENGTH_EPSILON_METERS);
      const shortSide = joist.kind === "long" ? coveredSides.find((side) => side.kind === "short") : undefined;
      const typeOffset = joist.kind === "short" ? 5 : 0;
      const nodeType: SupportNodeType = shortSide
        ? shortSide.coverage < intervalLength - REACTION_LENGTH_EPSILON_METERS
          ? 5
          : isEnd
            ? 11
            : 10
        : (((coveredSides.length >= 2 ? (isEnd ? 3 : 4) : isEnd ? 1 : 2) + typeOffset) as SupportNodeType);

      const point = orientation === "vertical" ? { x: cross, y: position } : { x: position, y: cross };

      return {
        point,
        nodeType,
        reactionKn: liveLoadKnPerSquareMeter * sides.reduce((total, side) => total + side.area, 0),
        terms: coveredSides.map((side) => ({ joistSpacing: side.distance, lengthAlongJoist: side.coverage })),
      };
    });
  });
};

// The one support per node type that the PDF's reference drawing labels (and highlights) - the first
// one found of each type. Shared so the drawing's highlighted dots and the PDF's number labels can
// never point at different supports.
export const getSupportNodeReferencePoints = (reactions: SupportReaction[]): Map<SupportNodeType, PlaneCoordinate> => {
  const points = new Map<SupportNodeType, PlaneCoordinate>();
  reactions.forEach((reaction) => {
    if (!points.has(reaction.nodeType)) {
      points.set(reaction.nodeType, reaction.point);
    }
  });
  return points;
};
